import { useState } from 'react';

// The SIP messages a trace produced, as a table. Shared by the Diagnostics rail's dialog and the
// line's Diagnostics tab, so there is one implementation of "what a traced message looks like".

/**
 * Asterisk stamps the ring in epoch seconds ("1787413435"). Shown as a clock time, because the
 * question being asked of this table is "what happened when I made that call", and comparing raw
 * epochs by eye is not a thing anybody does. Whole seconds is all the ring carries — no false ms.
 */
export function clockOf(ts) {
  const n = Number(ts);
  if (!Number.isFinite(n) || n <= 0) return String(ts ?? '');
  return new Date(n * 1000).toLocaleTimeString([], { hour12: false });
}

// 4xx AND WORSE IS NOT THE SAME AS WRONG. A 487 is the required answer to a CANCEL and a 486 is a
// busy phone; colouring them red had an operator hunting a fault in a call that behaved correctly.
const NORMAL_OUTCOMES = new Set(['401', '407', '487', '486', '603']);

/**
 * `mine` — the extensions belonging to the line being looked at. A row naming any other extension
 * is one the ADDRESS half of the filter caught: every device behind the traced router matches, so
 * a shared NAT puts a neighbour's message in this list. Marked rather than hidden, because hiding
 * it would break tracing by IP, which exists for devices whose extension cannot be matched.
 */
export default function SipMessageTable({ rows = [], mine = [] }) {
  const ours = new Set(mine.map(String));
  const userOf = (msg) => /sip:(\d{3,})@/.exec(String(msg))?.[1] ?? null;

  return (
    <table className="w-full border-collapse text-left font-mono text-xs">
      <thead>
        <tr className="border-b border-gray-200 text-[10px] uppercase tracking-wide text-gray-500">
          <th className="py-1 pr-3 font-medium">#</th>
          <th className="py-1 pr-3 font-medium">Time</th>
          <th className="py-1 pr-3 font-medium">Dir</th>
          <th className="py-1 pr-3 font-medium">Address</th>
          <th className="py-1 font-medium">Message</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => {
          const u = userOf(r.message);
          const foreign = u && !ours.has(u);
          const bad = r.status && Number(r.status) >= 400 && !NORMAL_OUTCOMES.has(String(r.status));
          return (
            <tr key={r.seq} className={`border-b border-gray-100 align-top ${foreign ? 'bg-amber-50' : ''}`}>
              <td className="py-1 pr-3 text-gray-400">{r.seq}</td>
              <td className="py-1 pr-3 whitespace-nowrap text-gray-500">{clockOf(r.ts)}</td>
              {/* Named, not arrows: "did the phone ever send a REGISTER" is the question, and
                  in/out is the answer to it. */}
              <td className={`py-1 pr-3 ${r.direction === 'in' ? 'text-blue-700' : 'text-gray-500'}`}>
                {r.direction === 'in' ? 'in' : 'out'}
              </td>
              <td className="py-1 pr-3 text-gray-600">{r.address}</td>
              <td className="py-1 whitespace-pre-wrap break-all">
                <span className={bad ? 'text-red-700' : 'text-gray-800'}>{r.message}</span>
                {foreign && (
                  <span className="ml-1 rounded bg-amber-100 px-1 text-[10px] text-amber-800">
                    ext {u}
                  </span>
                )}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

/**
 * The trace as text, for pasting into a ticket or an email.
 *
 * Kevin, 2026-08-22: "add ability to copy the trace log." He had already been doing this by hand —
 * the first real trace reached me as a pasted table — so the only question was whether the paste
 * carries its own caveats. It does: the header repeats which line, how much of the host buffer this
 * was, and the findings, because 43 rows with no denominator reads as the whole story when it is
 * 43 of 235. A foreign extension is marked inline for the same reason.
 */
export function traceAsText({ line, sip, mine }) {
  const ours = new Set((mine || []).map(String));
  const head = [
    `SIP messages · ${line.number || line.label || `line ${line.id}`}`,
    `${sip.matched} of ${sip.total_in_ring} messages in the host's buffer`,
    ...(sip.other_extensions?.length
      ? [`includes other extensions behind the same address: ${sip.other_extensions.join(', ')}`]
      : []),
    ...(sip.findings || []).map((f) => `- ${f.text}`),
    '',
    ['#', 'Time', 'Dir', 'Address', 'Message'].join('\t'),
  ];
  const body = (sip.rows || []).map((r) => {
    const u = /sip:(\d{3,})@/.exec(String(r.message))?.[1];
    const mark = u && !ours.has(u) ? `   [ext ${u} — not this line]` : '';
    return [r.seq, clockOf(r.ts), r.direction, r.address, r.message + mark].join('\t');
  });
  return [...head, ...body].join('\n');
}

export function CopyButton({ text, disabled }) {
  const [state, setState] = useState('idle');
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(text);
          setState('done');
          setTimeout(() => setState('idle'), 2000);
        } catch {
          // A denied clipboard is not a copied trace. Saying "Copied" here would be the same
          // class of lie as the rest of this card's bugs.
          setState('failed');
        }
      }}
      className="rounded border border-gray-300 px-2 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-40"
    >
      {state === 'done' ? 'Copied' : state === 'failed' ? 'Copy blocked — select the table' : 'Copy'}
    </button>
  );
}
